/** Section + modal for account deletion. Kept in one file – small and always used together. */
import { Box, Flex, Text, chakra } from '@chakra-ui/react';

export function DeleteAccountSection({ onDeleteClick }: { onDeleteClick: () => void }) {
  return (
    <Box
      bg="white"
      borderRadius="2xl"
      p="6"
      boxShadow="0 8px 24px rgba(0,0,0,0.12)"
      border="2px solid"
      borderColor="volyx.error"
    >
      <Text as="h3" fontSize="lg" fontWeight="semibold" color="volyx.error" mb="4">
        Danger Zone
      </Text>
      <chakra.button
        px="6"
        py="3"
        borderRadius="xl"
        fontWeight="semibold"
        transition="all 0.3s"
        cursor="pointer"
        border="1px solid"
        borderColor="volyx.error"
        bg="volyx.lightGray"
        color="volyx.error"
        _hover={{ bg: 'volyx.error', color: 'white', transform: 'scale(1.01)' }}
        onClick={onDeleteClick}
      >
        Delete Account
      </chakra.button>
      <Text fontSize="sm" color="volyx.textSecondary" mt="3">
        This action cannot be undone. All your data, events, and photos will be permanently deleted.
      </Text>
    </Box>
  );
}

export function DeleteAccountModal({
  isOpen,
  isDeleting,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!isOpen) return null;
  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.5)"
      align="center"
      justify="center"
      zIndex={1000}
      onClick={onClose}
    >
      <Box
        bg="white"
        borderRadius="2xl"
        p="6"
        maxW="md"
        w="full"
        mx="4"
        boxShadow="0 12px 48px rgba(0,0,0,0.15)"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Text as="h3" fontSize="xl" fontWeight="semibold" color="volyx.primary" mb="4">
          Delete Account
        </Text>
        <Text fontSize="md" color="volyx.error" fontWeight="semibold" mb="3">
          Are you sure you want to delete your account? This action cannot be undone.
        </Text>
        <Text fontSize="sm" color="volyx.textSecondary" mb="6" lineHeight="tall">
          This will permanently delete:
          <br />• Your profile and personal information
          <br />• All your events (if you're an organization)
          <br />• All your volunteer applications (if you're a volunteer)
          <br />• All your messages and notifications
          <br />• All your photos and files
        </Text>
        <Flex gap="3" justify="flex-end">
          <chakra.button
            px="6"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            transition="all 0.3s"
            cursor="pointer"
            border="none"
            bg="volyx.lightGray"
            color="volyx.text"
            _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </chakra.button>
          <chakra.button
            px="6"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            transition="all 0.3s"
            cursor="pointer"
            border="none"
            bg="volyx.error"
            color="white"
            boxShadow="0 4px 12px rgba(0,0,0,0.08)"
            _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)', bg: 'rgba(255,59,48,0.9)' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </chakra.button>
        </Flex>
      </Box>
    </Flex>
  );
}
